<template>
  <div
    class="conversations-list-wrap"
    :class="{
      hide: !showConversationList,
      'list--full-width': isOnExpandedLayout,
    }"
  >
    <pop-over-search :is-on-expanded-layout="isOnExpandedLayout" />
    <div
      class="chat-list__top"
      :class="{ filter__applied: hasAppliedFiltersOrActiveCustomView }"
    >
      <div class="flex-center chat-list__title wrapper">
        <h1
          class="page-sub-title text-truncate margin-bottom-0"
          :title="pageTitle"
          style="cursor: pointer"
          @mouseover="showFilters = true"
          @mouseleave="showFilters = false"
        >
          {{ pageTitle }}
          <span v-if="hasAppliedFilters && !chatListLoading">
            ({{ allCount }})
          </span>
        </h1>
        <selected-filters-view
          :show-filters="
            showFilters && Boolean(selectedFiltersViewItems.length)
          "
          :selected-filters="selectedFiltersViewItems"
          :conversation-type="conversationType"
        />
        <span
          v-if="
            !hasAppliedFiltersOrActiveCustomView &&
              conversationType !== 'archived'
          "
          class="conversation--status-pill"
        >
          {{
            this.$t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`)
          }}
        </span>
      </div>
      <div class="filter--actions">
        <div v-if="hasAppliedFilters && !hasActiveCustomView">
          <woot-button
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            size="small"
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="clearAdvancedFilters"
          />
        </div>
        <div v-if="hasActiveCustomView">
          <woot-button
            v-if="isRoleAdmin || currentUser.id === activeCustomView.user_id"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            size="small"
            variant="smooth"
            color-scheme="alert"
            icon="delete"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            fill-color="none"
            @click="showDeleteCustomViewModal = true"
          />
        </div>
        <woot-button
          v-else
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          v-filtered-circle="isFiltered"
          variant="smooth"
          color-scheme="secondary"
          icon="filter"
          size="small"
          view-box="0 0 20 20"
          @click="onToggleAdvanceFiltersModal"
        />
        <conversation-basic-filter
          v-if="!hasAppliedFiltersOrActiveCustomView"
          :conversation-type="conversationType"
          @changeFilter="onBasicFilterChange"
        />
        <woot-button
          v-if="!hasActiveCustomView"
          v-tooltip.right="$t('FILTER.NEW_CONVERSATION_TOOLTIP')"
          variant="smooth"
          color-scheme="secondary"
          icon="add"
          size="small"
          @click="onOpenNewConversation"
        />
        <new-email-conversation
          v-if="isOpenNewEmailConversation"
          :show="isOpenNewEmailConversation"
          :inboxes="channelInboxes"
          @on-close="onCloseModal"
        />
      </div>
    </div>

    <add-custom-views
      v-if="addCustomViewModal"
      :custom-views-query="customViewQuery"
      @close="closeCustomViewModal"
    />

    <delete-custom-views
      v-if="showDeleteCustomViewModal"
      :show-delete-popup.sync="showDeleteCustomViewModal"
      :active-custom-view="activeCustomView"
      :custom-views-id="customViewId"
      :open-last-item-after-delete="openHomeAfterDeleter"
      @close="showDeleteCustomViewModal = false"
    />

    <chat-type-tabs
      v-if="!hasAppliedFiltersOrActiveCustomView"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      class="tab--chat-type"
      @chatTabChange="updateAssigneeTab"
    />

    <p v-if="!chatListLoading && !conversationList.length" class="content-box">
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <conversation-bulk-actions
      v-if="selectedConversations.length && conversationType !== 'archived'"
      :conversations="selectedConversations"
      :selected-inboxes="selectedInboxes"
      :conversation-list="conversationList"
      @select-all-conversations="selectAllConversations"
      @clear="selectedConversations = []"
    />
    <div
      ref="activeConversation"
      class="conversations-list"
      :class="{ 'is-context-menu-open': isContextMenuOpen }"
    >
      <recycle-scroller
        ref="scroller"
        v-slot="{ item }"
        class="recycleview"
        :class="{ 'scroll-end': scrollEnd }"
        :items="conversationList"
        :item-size="115"
        key-field="id"
        @scroll.native="onScroll"
      >
        <conversation-card
          v-if="!item.isHidden"
          :key="item.id"
          :active-label="label"
          :team-id="teamId"
          :folders-id="customViewId"
          :chat="item"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :selected="isConversationSelected(item.id)"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
          @context-menu-toggle="onContextMenuToggle"
          @context-menu-data="setContextMenuData"
        />
      </recycle-scroller>
      <div v-if="scrollEnd" class="scroll-end">
        <div v-if="chatListLoading" class="text-center">
          <span class="spinner" />
        </div>

        <woot-button
          v-if="!hasCurrentPageEndReached && !chatListLoading && hasMoreItems"
          variant="clear"
          size="expanded"
          @click="loadMoreConversations"
        >
          {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
        </woot-button>

        <p
          v-if="showEndOfListMessage"
          class="text-center text-muted end-of-list-text"
        >
          {{ $t('CHAT_LIST.EOF') }}
        </p>
      </div>
    </div>
    <woot-context-menu
      v-if="showContextMenu && contextMenuData"
      ref="menu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <conversation-context-menu
        :conversation-id="contextConversationId"
        :status="contextMenuData.chat.status"
        :inbox-id="contextMenuData.inbox.id"
        :priority="contextMenuData.chat.priority"
        :has-unread-messages="contextMenuData.hasUnread"
        :context-menu-y="contextMenu.y"
        @close="closeContextMenu"
      />
    </woot-context-menu>
    <woot-modal
      :show.sync="showAdvancedFilters"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
    >
      <conversation-advanced-filter
        v-if="showAdvancedFilters"
        :initial-filter-types="advancedFilterTypes"
        :initial-applied-filters="appliedFilter"
        :on-close="closeAdvanceFiltersModal"
        :conversation-type="conversationType"
        @applyFilter="onApplyFilter"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import wootConstants from 'dashboard/constants/globals';
import { RecycleScroller } from 'vue-virtual-scroller';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import { throttle } from 'lodash';

import alertMixin from 'shared/mixins/alertMixin';
import filterMixin from 'shared/mixins/filterMixin';
import rtlMixin from 'shared/mixins/rtlMixin';

import PopOverSearch from '../../dashboard/routes/dashboard/conversation/search/PopOverSearch';
import ConversationAdvancedFilter from './widgets/conversation/ConversationAdvancedFilter';
import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter';
import ChatTypeTabs from './widgets/ChatTypeTabs';
import ConversationCard from './widgets/conversation/ConversationCard';
import advancedFilterTypes from './widgets/conversation/advancedFilterItems';
import SelectedFiltersView from './ui/SelectedFiltersView.vue';
import AddCustomViews from 'dashboard/routes/dashboard/customviews/AddCustomViews';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import ConversationContextMenu from './widgets/conversation/contextMenu/Index.vue';
import { INBOX_TYPES } from '../../shared/mixins/inboxMixin';
import NewEmailConversation from '../routes/dashboard/conversation/NewEmailConversation.vue';

export default {
  components: {
    NewEmailConversation,
    SelectedFiltersView,
    AddCustomViews,
    ChatTypeTabs,
    ConversationCard,
    ConversationAdvancedFilter,
    DeleteCustomViews,
    ConversationBulkActions,
    ConversationBasicFilter,
    RecycleScroller,
    PopOverSearch,
    ConversationContextMenu,
  },
  mixins: [alertMixin, filterMixin, rtlMixin],
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    customViewId: {
      type: [String, Number],
      default: 0,
    },
    showConversationList: {
      default: true,
      type: Boolean,
    },
    isOnExpandedLayout: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isOpenNewEmailConversation: false,
      activeAssigneeTab: wootConstants.ASSIGNEE_TYPE.ME,
      activeStatus: wootConstants.STATUS_TYPE.OPEN,
      activeSortBy: wootConstants.SORT_BY_TYPE.LATEST,
      activeOrder: wootConstants.ORDER_TYPE.DESCENDING,
      selectedConversations: [],
      advancedFilterTypes: advancedFilterTypes.map(filter => {
        const newFilter = { ...filter };

        if (newFilter?.attributeKey === 'created_at') {
          newFilter.filterOperators = [
            ...newFilter.filterOperators,
            { value: 'between', label: 'Between' },
          ];
        }

        return {
          ...newFilter,
          attributeName: this.$t(
            `FILTER.ATTRIBUTES.${newFilter.attributeI18nKey}`
          ),
        };
      }),
      // chatsOnView is to store the chats that are currently visible on the screen,
      // which mirrors the conversationList.
      chatsOnView: [],
      customViewQuery: {},
      showDeleteCustomViewModal: false,
      selectedInboxes: [],
      isContextMenuOpen: false,
      isFiltered: false,
      allCount: 0,
      appliedFilter: [],
      scrollEnd: false,
      hasMoreItems: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
      contextMenuData: null,
      showFilters: false,
      selectedFilters: [],
      abortController: null,
      metaAbortController: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      chatLists: 'getAllConversations',
      mineChatsList: 'getMineChats',
      allChatList: 'getAllStatusChats',
      unAssignedChatsList: 'getUnAssignedChats',
      chatListLoading: 'getChatListLoadingStatus',
      activeInbox: 'getSelectedInbox',
      conversationStats: 'conversationStats/getStats',
      appliedFilters: 'getAppliedConversationFilters',
      customViews: 'customViews/getCustomViews',
      inboxes: 'inboxes/getInboxes',
      liveUpdateTiming: 'accounts/getLiveUpdateTiming',
      addCustomViewModal: 'customViews/getAddCustomViewModal',
      showAdvancedFilters: 'customViews/getShowAdvancedFilters',
      customViewInit: 'customViews/getCustomViewInit',
      conditions: 'getConversationListConditions',
      inboxesList: 'inboxes/getInboxes',
      current_user: 'getCurrentUser',
      channelInboxes: 'inboxes/getEmailInboxes',
      chatStatusFilter: 'getChatStatusFilter',
    }),
    isRoleAdmin() {
      return this.currentUser.role === 'administrator';
    },
    selectedFiltersViewItems() {
      return this.activeCustomView
        ? this.activeCustomView.query.payload
        : this.selectedFilters;
    },
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    contextConversationId() {
      return this.contextMenuData ? this.contextMenuData.chat.id : null;
    },
    hasAppliedFilters() {
      return this.appliedFilters.length !== 0;
    },
    hasActiveCustomView() {
      return this.activeCustomView && this.customViewId !== 0;
    },
    hasAppliedFiltersOrActiveCustomView() {
      return this.hasAppliedFilters || this.hasActiveCustomView;
    },
    showEndOfListMessage() {
      return (
        this.conversationList.length &&
        this.hasCurrentPageEndReached &&
        !this.chatListLoading
      );
    },
    assigneeTabItems() {
      return this.updateCounts();
    },
    showAssigneeInConversationCard() {
      return (
        this.hasAppliedFiltersOrActiveCustomView ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
      );
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },
    currentPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.activeAssigneeTab
      );
    },
    currentPageFilterKey() {
      return this.hasAppliedFiltersOrActiveCustomView
        ? 'appliedFilters'
        : this.activeAssigneeTab;
    },
    currentFiltersPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.currentPageFilterKey
      );
    },
    hasCurrentPageEndReached() {
      return this.$store.getters['conversationPage/getHasEndReached'](
        this.currentPageFilterKey
      );
    },
    activeAssigneeTabCount() {
      const { activeAssigneeTab } = this;
      const count = this.assigneeTabItems.find(
        item => item.key === activeAssigneeTab
      ).count;
      return count;
    },
    conversationFilters() {
      return {
        inboxId: this.inboxId ? this.inboxId : undefined,
        assigneeType: this.activeAssigneeTab,
        status: this.activeStatus,
        sortBy: this.activeSortBy,
        order: this.activeOrder,
        page: this.conversationListPagination,
        labels: this.label ? [this.label] : undefined,
        teamId: this.teamId || undefined,
        conversationType: this.conversationType || undefined,
        folders: this.hasActiveCustomView ? {} : undefined,
      };
    },
    conversationListPagination() {
      const conversationsPerPage = 25;
      const isNoFiltersOrCustomViewsAndChatListNotEmpty =
        !this.hasAppliedFiltersOrActiveCustomView && this.chatsOnView !== [];
      const isUnderPerPage =
        this.chatsOnView.length < conversationsPerPage &&
        this.activeAssigneeTabCount < conversationsPerPage &&
        this.activeAssigneeTabCount > this.chatsOnView.length;

      if (isNoFiltersOrCustomViewsAndChatListNotEmpty && isUnderPerPage) {
        return 1;
      }
      return this.currentPage + 1;
    },
    pageTitle() {
      if (this.hasAppliedFilters) {
        return this.$t('CHAT_LIST.TAB_HEADING');
      }
      if (this.inbox.name) {
        return this.inbox.name;
      }
      if (this.activeTeam.name) {
        return this.activeTeam.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      if (this.conversationType === 'mention') {
        return this.$t('CHAT_LIST.MENTION_HEADING');
      }
      if (this.conversationType === 'participating') {
        return this.$t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
      }
      if (this.conversationType === 'unattended') {
        return this.$t('CHAT_LIST.UNATTENDED_HEADING');
      }
      if (this.conversationType === 'archived') {
        return this.$t('CHAT_LIST.ARCHIVED_HEADING');
      }
      if (this.hasActiveCustomView) {
        return this.activeCustomView.name;
      }
      return this.$t('CHAT_LIST.TAB_HEADING');
    },
    conversationList() {
      let conversationList = [];
      if (!this.hasAppliedFiltersOrActiveCustomView) {
        const filters = this.conversationFilters;
        if (this.activeAssigneeTab === 'me') {
          conversationList = [...this.mineChatsList(filters)];
        } else if (this.activeAssigneeTab === 'unassigned') {
          conversationList = [...this.unAssignedChatsList(filters)];
        } else {
          conversationList = [...this.allChatList(filters)];
        }
      } else {
        conversationList = [...this.chatLists];
      }

      if (this.conversationType === 'archived') {
        conversationList = conversationList.filter(c => c.archived);
      }

      const conversationListWithoutHIdden = conversationList.filter(
        item => !item.isHidden
      );
      return conversationListWithoutHIdden;
    },
    activeCustomView() {
      if (this.customViewId) {
        const activeView = this.customViews.filter(
          view => view.id === Number(this.customViewId)
        );
        const [firstValue] = activeView;
        return firstValue;
      }
      return undefined;
    },
    activeTeam() {
      return this.teamId
        ? this.$store.getters['teams/getTeam'](this.teamId)
        : {};
    },
  },
  watch: {
    liveUpdateTiming(newVal, oldVal) {
      if (oldVal !== -1) {
        bus.$off('fetch_conversation_stats', this.getStats);
      }
      if (newVal !== -1) {
        this.getStats = throttle(data => {
          this.fetchConversationsMeta(data.conditions, data.filters);
        }, newVal);

        bus.$on('fetch_conversation_stats', this.getStats);
      }
    },
    hasAppliedFiltersOrActiveCustomView(newValue) {
      this.isFiltered = !!newValue;
    },
    teamId() {
      this.$store.dispatch('setConversationListConditions', {
        type: 'team',
        teamId: this.teamId,
      });
      this.clearAdvancedFilters();
      this.resetAndFetchData();
    },
    inboxId() {
      this.$store.dispatch('setConversationListConditions', {
        type: 'inbox',
        inboxId: this.inboxId,
      });
      this.clearAdvancedFilters();
      this.resetAndFetchData();
    },
    label() {
      this.$store.dispatch('setConversationListConditions', {
        type: 'label',
        label: this.label,
      });
      this.clearAdvancedFilters();
      this.resetAndFetchData();
    },
    conversationType() {
      this.$store.dispatch('setConversationListConditions', {
        type: this.conversationType,
        userId: this.currentUser?.id,
      });
      this.resetAndFetchData();
    },
    activeCustomView() {
      this.$store.dispatch('setConversationListConditions', {
        type: 'custom_view',
        customViewId: this.customViewId,
      });
      this.resetAndFetchData();
    },
    chatLists() {
      this.chatsOnView = this.conversationList;
    },
  },
  updated() {
    this.updateCounts();
    this.evaluateLoadMoreCondition();
  },
  mounted() {
    if (!this.hasAccounts) return;
    this.manageInitialFIlters();
    this.$store.dispatch('sla/get');
    this.$store.dispatch('agents/get');
    this.resetAndFetchData();
  },
  methods: {
    onCloseModal() {
      this.isOpenNewEmailConversation = false;
    },
    onOpenNewConversation() {
      const hasEmailChannel = this.inboxesList.some(
        inbox => inbox.channel_type === INBOX_TYPES.EMAIL
      );
      if (hasEmailChannel) {
        this.isOpenNewEmailConversation = true;
      } else {
        this.showAlert(
          'To start an email conversation, please make sure an email channel is configured.',
          'default',
          {
            type: 'link',
            to: `/app/accounts/${this.current_user.account_id}/settings/inboxes/new`,
            message: 'Go to Channel Settings',
          },
          'Email Channel Not Configured '
        );
      }
    },
    manageInitialFIlters() {
      const basicFilterStatus = LocalStorage.get('basicFilter.status');
      const basicFilterSort = LocalStorage.get('basicFilter.sort');
      const basicFilterOrder = LocalStorage.get('basicFilter.order');
      if (basicFilterStatus) this.activeStatus = basicFilterStatus;
      if (basicFilterSort) this.activeSortBy = basicFilterSort;
      if (basicFilterOrder) this.activeOrder = basicFilterOrder;
      const hasFilterValues = localStorage.getItem(
        'advancedConversationFilters'
      );
      this.selectedFilters = hasFilterValues ? JSON.parse(hasFilterValues) : [];
      if (hasFilterValues) {
        this.isFiltered = true;
      }
      this.activeAssigneeTab =
        LocalStorage.get('activeAssigneeTab') || wootConstants.ASSIGNEE_TYPE.ME;

      this.$store.dispatch('setChatStatusFilter', this.activeStatus);
      this.$store.dispatch('setChatSortFilter', this.activeSortBy);
      this.$store.dispatch('setChatOrderFilter', this.activeOrder);
    },
    evaluateLoadMoreCondition() {
      const scrollWrapperHeight = this.$refs.scroller.$refs?.wrapper
        ?.clientHeight;
      const conversationListHeight = this.$refs.activeConversation
        ?.offsetHeight;
      this.hasMoreItems = scrollWrapperHeight >= conversationListHeight;
    },
    onApplyFilter(payload, customViewInit = false) {
      if (customViewInit) {
        this.customViewQuery = filterQueryGenerator(payload);
        this.$store.commit('customViews/SET_ADD_CUSTOM_VIEW_MODAL', true);
        this.$store.commit('customViews/SET_SHOW_ADVANCED_FILTERS', false);
      } else {
        this.resetBulkActions();
        this.selectedFilters = payload;
        this.$store.dispatch('conversationPage/reset');
        this.$store.dispatch('emptyAllConversations');
        this.fetchFilteredConversations(payload);
        this.fetchConversationsMeta();

        LocalStorage.set('advancedConversationFilters', payload);
      }
    },
    updateCounts() {
      const ASSIGNEE_TYPE_TAB_KEYS = {
        me: 'mineCount',
        unassigned: 'unAssignedCount',
        all: 'allCount',
      };
      return Object.keys(ASSIGNEE_TYPE_TAB_KEYS).map(key => {
        const count = this.conversationStats[ASSIGNEE_TYPE_TAB_KEYS[key]];
        if (key === 'all') {
          this.allCount = count;
        }
        return {
          key,
          name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
          count,
        };
      });
    },
    onScroll() {
      if (!this.$refs.scroller) return;
      const el = this.$refs.scroller.$el;
      const parent = this.$refs.activeConversation;
      this.scrollEnd =
        parent.offsetHeight + el.scrollTop >= el.scrollHeight - 10;
    },
    closeCustomViewModal() {
      this.$store.commit('customViews/SET_ADD_CUSTOM_VIEW_MODAL', false);
    },
    onToggleAdvanceFiltersModal() {
      this.$store.commit('customViews/SET_CUSTOM_VIEW_INIT', false);
      if (!this.hasAppliedFilters) {
        this.initializeExistingFilterToModal();
      }
      this.$store.commit('customViews/SET_SHOW_ADVANCED_FILTERS', true);
    },
    closeAdvanceFiltersModal() {
      this.$store.commit('customViews/SET_SHOW_ADVANCED_FILTERS', false);
      this.appliedFilter = [];
    },
    clearAdvancedFilters() {
      LocalStorage.remove('advancedConversationFilters');
      this.appliedFilter = [];
      this.selectedFilters = [];
      this.isFiltered = false;
      this.resetAndFetchData();
    },
    resetAndFetchData() {
      this.resetBulkActions();
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.$store.dispatch('clearConversationFilters');
      this.$store.dispatch('clearConversationFilters');
      this.$store.dispatch('conversationStats/set', {
        mine_count: null,
        unassigned_count: null,
        all_count: null,
      });

      if (this.hasActiveCustomView) {
        const payload = this.activeCustomView.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.customViewId) {
        return;
      }

      /* restore last state */
      const advancedConversationFilters = LocalStorage.get(
        'advancedConversationFilters'
      );
      if (advancedConversationFilters) {
        this.$store.dispatch(
          'setConversationFilters',
          advancedConversationFilters
        );
        this.fetchFilteredConversations(advancedConversationFilters);
      } else {
        this.fetchConversations();
        this.appliedFilter = [];
      }
      this.fetchConversationsMeta();
    },
    conversationLoad() {
      this.onScroll();
      this.$emit('conversation-load');
    },
    async fetchConversations() {
      this.initAbortController();
      this.$store
        .dispatch('fetchAllConversations', {
          params: this.conversationFilters,
          signal: this.abortController.signal,
        })
        .then(data => {
          if (data) this.conversationLoad();
        });
    },
    getActiveTab() {
      switch (this.$route.name) {
        case 'conversation_mentions':
        case 'conversation_through_mentions':
          return 'mention';
        case 'conversation_unattended':
        case 'conversation_through_unattended':
          return 'unattended';
        case 'conversation_archived':
        case 'conversation_through_archived':
          return 'archived';
        default:
          return '';
      }
    },
    async fetchConversationsMeta(conditions = null, filters = null) {
      // using source instead of computed values as throttled function doesnot have latest data
      const conversationTab = this.getActiveTab();
      this.initMetaAbortController();

      const filtersSource = filters || this.appliedFilters;
      const filterData = filtersSource.length
        ? filterQueryGenerator(this.normalizeStringToTimestamp(filtersSource))
        : null;

      const conditionsSource = conditions || this.conditions;
      const queryParams = {};
      switch (conditionsSource?.type) {
        case 'inbox':
          queryParams.inboxId = conditionsSource.inboxId;
          break;
        case 'team':
          queryParams.teamId = conditionsSource.teamId;
          break;
        case 'label':
          queryParams.labels = [conditionsSource.label];
          break;
        default:
          break;
      }

      const latestParams = { ...this.conversationFilters };
      const isArchived = this.conversationType === 'archived';
      const signal = this.metaAbortController.signal;

      await this.$store.dispatch('conversationStats/get', {
        params: latestParams,
        queryParams,
        appliedFilters: filterData,
        archived: isArchived,
        conversationTab,
        signal,
      });

      return this.updateCounts();
    },

    initMetaAbortController() {
      if (this.metaAbortController) this.metaAbortController.abort();
      this.metaAbortController = new AbortController();
    },
    initAbortController() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
    },
    loadMoreConversations() {
      if (!this.hasAppliedFiltersOrActiveCustomView) {
        this.fetchConversations();
      }
      if (this.hasActiveCustomView) {
        const payload = this.activeCustomView.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.hasAppliedFilters) {
        this.fetchFilteredConversations(this.appliedFilters);
      }
    },
    fetchFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: filterQueryGenerator(
            this.normalizeStringToTimestamp(payload)
          ),
          page,
          archived: this.conversationType === 'archived',
          conversationType: this.conversationType,
        })
        .then(() => this.conversationLoad());
      this.$store.commit('customViews/SET_SHOW_ADVANCED_FILTERS', false);
    },
    fetchSavedFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: payload,
          page,
          archived: this.conversationType === 'archived',
        })
        .then(() => this.conversationLoad());
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        LocalStorage.set('activeAssigneeTab', selectedTab);
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeAssigneeTab = selectedTab;
        if (!this.currentPage) {
          this.fetchConversations();
          this.fetchConversationsMeta();
        }
      }
    },
    resetBulkActions() {
      this.selectedConversations = [];
      this.selectedInboxes = [];
    },
    onBasicFilterChange(value, type) {
      if (type === 'status') {
        this.activeStatus = value;
      } else if (type === 'sort') {
        this.activeSortBy = value;
      } else {
        this.activeOrder = value;
      }
      LocalStorage.set(`basicFilter.${type}`, value);
      this.resetAndFetchData();
    },
    openHomeAfterDeleter() {
      this.$router.push({ name: 'home' });
    },
    isConversationSelected(id) {
      return this.selectedConversations.includes(id);
    },
    selectConversation(conversationId, inboxId) {
      this.selectedConversations.push(conversationId);
      this.selectedInboxes.push(inboxId);
    },
    deSelectConversation(conversationId, inboxId) {
      this.selectedConversations = this.selectedConversations.filter(
        item => item !== conversationId
      );
      this.selectedInboxes = this.selectedInboxes.filter(
        item => item !== inboxId
      );
    },
    selectAllConversations(check) {
      if (check) {
        this.selectedConversations = this.conversationList.map(item => item.id);
        this.selectedInboxes = this.conversationList.map(item => item.inbox_id);
      } else {
        this.resetBulkActions();
      }
    },
    openContextMenu(e) {
      this.contextMenu.x = e.pageX || e.clientX;
      this.contextMenu.y = e.pageY || e.clientY;
      if (this.isRTLView) {
        this.contextMenu.x = (e.pageX || e.clientX) - 180;
      }
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onContextMenuToggle(state, e = null) {
      this.isContextMenuOpen = state;
      if (state) {
        this.openContextMenu(e);
      } else {
        this.closeContextMenu();
      }
    },
    setContextMenuData(data) {
      this.contextMenuData = data;
    },
  },
};
</script>
